import { useCallback, useContext, useLayoutEffect, useState } from 'react';
import { EDCard } from '../../../../BaseComponents/Card/EDCard';
import EDGrid from '../../../../BaseComponents/GridComponents/EDGrid';
import { IMasterDetailBaseProps } from '../../../../Types/MasterDetailTypes';
import { SideBySideWidthType } from '../../../../Utils/ObjektVyUtils';
import { IClientBusinessModel } from '../../../../Types/ClientModelTypes';
import { makeStyles } from 'tss-react/mui';
import EDTypography from '../../../../BaseComponents/Typography/EDTypography';
import { VisningsTypContext } from '../../../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/VisningsTypContext/VisningsTypContext';
import { ShowDetailsRightSizeType } from '../../../../Features/Delsystem/_Common/Const/Delsystem';
import { selectedId } from '../../MasterDetailUtils';
import { LocalBreakpointContextProvider } from '../../../BreakpointContext/LocalBreakpointContextProvider';
import { MdDetailSelectionContextProvider } from '../../Contexts/MdDetailSelectionContext';
import { useDetailNavigation } from '../../Hooks/useDetailNavigation';
import { Route, Routes, useParams } from 'react-router';

const useStyles = makeStyles()(theme => ({
  noSelectedText: {
    color: theme.customProperties.Fade.DarkOnLight.mediumEmphasis,
  },
  noSelectedContainer: {
    height: '100%',
    width: '100%',
    display: 'Flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  gridItem: {
    height: '100%',
    overflowX: 'hidden',
  },
  leftGridItemSizeInPixels: {
    flexGrow: 1,
  },
}));

interface IMdDetailFirstBaseProps extends IMasterDetailBaseProps {
  collection: IClientBusinessModel;
  size?: ShowDetailsRightSizeType;
  detailRouteName?: string;
}

interface IMdDetailRightActivatorProps {
  idName: string;
  onIdChange: (id: string | null) => void;
}

// Defined outside MdDetailRight to keep component reference stable.
const MdDetailRightActivator: React.FC<IMdDetailRightActivatorProps> = ({
  idName,
  onIdChange,
}) => {
  const params = useParams();
  const idParam = params[idName] ?? null;

  useLayoutEffect(() => {
    onIdChange(idParam);
  }, [idParam, onIdChange]);

  useLayoutEffect(() => {
    return () => onIdChange(null);
  }, [onIdChange]);

  return null;
};

const defaultShowDetailsRightRightSize = 4;

/**
 * Komponent som visar en master-detail relation. Detaljer visas till höger om Master-komponenten
 */
export const MdDetailRight = (props: IMdDetailFirstBaseProps) => {
  const { classes, cx } = useStyles();
  const detailRoute = props.detailRouteName ? `${props.detailRouteName}/` : '';
  const idName = props.detailRouteName ? `${props.detailRouteName}_id` : 'id';
  const [activeIdParam, setActiveIdParam] = useState<string | null>(null);
  const handleIdChange = useCallback(
    (id: string | null) => setActiveIdParam(id),
    []
  );
  const selectedDetailId = props.selectedDetailId ?? activeIdParam;

  const Master = props.master.comp;
  const Detail = props.detail.comp;

  const {
    selectNextDetail,
    selectPreviousDetail,
    indexCaption,
    currentItem,
    totalItems,
  } = useDetailNavigation(
    selectedDetailId,
    props.selectDetail,
    props.collection
  );

  const collectionSOLabel =
    props.collection?.RootType.SOLabel.toLowerCase() || 'post';

  const visningsTypContext = useContext(VisningsTypContext);

  const sizeInPixels = typeof props.size === 'string' ? props.size : null;
  const sizeInGridSize = typeof props.size === 'number' ? props.size : null;

  const rightGridSize = sizeInGridSize
    ? sizeInGridSize
    : defaultShowDetailsRightRightSize;
  const leftGridSize = sizeInPixels
    ? true
    : ((12 - rightGridSize) as SideBySideWidthType);

  return (
    <EDGrid style={{ height: '100%', overflowY: 'hidden' }} container>
      <Routes>
        <Route
          path={`${detailRoute}:${idName}`}
          element={
            <MdDetailRightActivator
              idName={idName}
              onIdChange={handleIdChange}
            />
          }
        >
          <Route path="*" element={null} />
        </Route>
      </Routes>
      <EDGrid
        className={cx(classes.gridItem, {
          [classes.leftGridItemSizeInPixels]: !!sizeInPixels,
        })}
        item
        xs={12}
        md={leftGridSize}
      >
        {visningsTypContext.visningsTyp === 'kort' ? (
          <Master
            selectDetail={props.selectDetail}
            selectedDetail={selectedDetailId}
            selectNextDetail={selectNextDetail}
            selectPreviousDetail={selectPreviousDetail}
          />
        ) : (
          <EDCard height="calc(100% - 2px)" width="calc(100% - 2px)">
            <Master
              selectDetail={props.selectDetail}
              selectedDetail={selectedDetailId}
              selectNextDetail={selectNextDetail}
              selectPreviousDetail={selectPreviousDetail}
            />
          </EDCard>
        )}
      </EDGrid>
      <EDGrid
        className={classes.gridItem}
        item
        xs={12}
        md={sizeInPixels ? 12 : rightGridSize}
        style={sizeInPixels ? { flexBasis: sizeInPixels } : undefined}
      >
        <LocalBreakpointContextProvider breakpoint="sm">
          <EDCard height="calc(100% - 2px)" width="calc(100% - 2px)">
            {selectedDetailId ? (
              <MdDetailSelectionContextProvider
                selectedDetail={selectedDetailId}
                selectDetail={props.selectDetail}
                selectNextDetail={selectNextDetail}
                selectPreviousDetail={selectPreviousDetail}
                indexCaption={indexCaption}
                currentItem={currentItem}
                totalItems={totalItems}
              >
                <Detail
                  id={selectedDetailId}
                  key={selectedId(selectedDetailId)}
                />
              </MdDetailSelectionContextProvider>
            ) : (
              <div className={classes.noSelectedContainer}>
                <EDTypography className={classes.noSelectedText} component="h2">
                  Ingen {collectionSOLabel} vald
                </EDTypography>
              </div>
            )}
          </EDCard>
        </LocalBreakpointContextProvider>
      </EDGrid>
    </EDGrid>
  );
};
